import React, { useEffect, useState } from "react";
import { ShieldCheck, AlertTriangle, RefreshCw, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { getErrorMessage } from "../lib/api";

const STATUS_LABEL = { verified: "Verified", pending: "Pending DNS", not_started: "Not started", failed: "Failed", temporary_failure: "Retrying", not_added: "Not added in Resend", error: "Check failed", unknown: "Unknown" };

// Live Resend sending-domain status for the configured sender.
const EmailDomainStatus = () => {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = (refresh = false) => api.get(`/account/email-domain${refresh ? "?refresh=1" : ""}`).then((r) => setInfo(r.data)).catch(() => setInfo(null));
  useEffect(() => { load(); }, []);

  const recheck = async () => {
    setBusy(true);
    try {
      const r = await api.post("/account/email-domain/verify");
      setInfo(r.data);
      toast[r.data.verified ? "success" : "message"](r.data.verified ? `${r.data.domain} is verified` : `${r.data.domain}: ${STATUS_LABEL[r.data.status] || r.data.status}`);
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not check domain"));
    } finally { setBusy(false); }
  };

  const copy = async (v) => { try { await navigator.clipboard.writeText(v); toast.success("Copied"); } catch { window.prompt("Copy:", v); } };

  if (!info) return null;
  const ok = info.verified;
  return (
    <div className={`mt-6 rounded-2xl border p-4 text-sm ${ok ? "bg-[#e8f8ef] border-[#63c79a] text-[#0d4d33]" : "bg-[#fff8e1] border-[#f3c932] text-[#5a4600]"}`} data-testid="email-domain-status">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        {ok ? <ShieldCheck className="flex-shrink-0 text-[#2e8b57]" size={20} /> : <AlertTriangle className="flex-shrink-0 text-[#f7931e]" size={20} />}
        <div className="flex-1">
          <p className="font-bold" data-testid="email-domain-headline">
            {ok ? `Emails send from ${info.sender}` : `Sending domain ${info.domain}: ${STATUS_LABEL[info.status] || info.status}`}
          </p>
          <p className="mt-0.5 text-xs opacity-80">
            {ok ? `${info.domain} is verified in Resend - invites, reminders and reports reach real inboxes.` : `Until it verifies, emails go out from ${info.fallback_sender} (Resend test sender, delivers only to your own Resend account email).`}
            {info.error && <span className="block mt-1 break-words">Error: {info.error}</span>}
          </p>
        </div>
        <button onClick={recheck} disabled={busy} className={`btn-pill text-sm inline-flex items-center gap-1 disabled:opacity-60 ${ok ? "bg-white text-[#2e8b57] border border-[#63c79a]" : "bg-[#f7931e] hover:bg-[#e0841a] text-white"}`} data-testid="email-domain-recheck">
          <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> {busy ? "Checking..." : "Re-check"}
        </button>
      </div>
      {!ok && info.records?.length > 0 && (
        <div className="mt-4 overflow-x-auto" data-testid="email-domain-records">
          <p className="text-xs font-bold uppercase tracking-widest mb-2">Add these DNS records at your registrar</p>
          <table className="w-full text-xs">
            <thead><tr className="text-left"><th className="py-1 pr-3">Type</th><th className="py-1 pr-3">Name</th><th className="py-1 pr-3">Value</th><th className="py-1">Status</th></tr></thead>
            <tbody>
              {info.records.map((r, i) => (
                <tr key={i} className="border-t border-[#f3c932]/40 align-top">
                  <td className="py-1.5 pr-3 font-mono">{r.type}</td>
                  <td className="py-1.5 pr-3 font-mono break-all">{r.name}</td>
                  <td className="py-1.5 pr-3 font-mono break-all">{r.value} <button onClick={() => copy(r.value)} className="inline-flex align-middle text-[#1b8bc9] hover:text-[#e5356f]" aria-label="Copy value"><Copy size={12} /></button></td>
                  <td className="py-1.5 font-semibold">{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EmailDomainStatus;
